import {
  Box,
  BoxProps,
  IconButton,
  Stack,
  StackProps,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { AircraftRestriction, AircraftType, CargoType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import {
  aircraftTypeOptions,
  getPrettyFlightSegmentBookingStatus,
  getPrettyIATAFlightSegmentStatus,
  miToKm,
  modelUtils,
  TimeZone,
} from '@pelicargo/utils';
import { FlightSegmentBookingStatus } from '@prisma/client';
import { ReactNode, useMemo } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { HiPlus, HiTrash } from 'react-icons/hi';
import { HiMiniCheckCircle, HiMiniPauseCircle, HiMiniQuestionMarkCircle, HiMiniXCircle } from 'react-icons/hi2';

import { useCreateFlightSegment } from '../../hooks/flightSegment/useCreate';
import { useDelete } from '../../hooks/flightSegment/useDelete';
import { useFlightSegmentsForm } from '../../hooks/flightSegment/useForm';
import { useUpdateFlightSegment } from '../../hooks/flightSegment/useUpdate';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { distance } from '../../utils/gis';
import { FlightSegment } from '../../utils/types';
import { AlertModal } from '../AlertModal/AlertModal';
import { AircraftInput } from '../Autocomplete/AircraftInput';
import { AirlineInput } from '../Autocomplete/AirlineInput';
import { AirportInput } from '../Autocomplete/AirportInput';
import { Label, LabeledText } from '../LabeledText';
import { SimpleForm } from '../SimpleForm';
import { SimpleInput } from '../SimpleInput';
import { SimpleLabeledPopoverInput } from '../SimpleLabeledPopoverInput';
import { SimplePopoverInput } from '../SimplePopoverInput';
import { SimpleSelect } from '../SimpleSelect';
import { SimplePopoverUnifiedInput } from '../SimpleUnifiedPopoverInput';

type Props = {
  flightSegment: FlightSegment;
  defaultOriginTimezone: TimeZone;
  defaultDestinationTimezone: TimeZone;
  prevDestination: string;
  nextOrigin: string;
};

export const FlightSegmentRow = ({
  flightSegment,
  defaultOriginTimezone,
  defaultDestinationTimezone,
  prevDestination,
  nextOrigin,
}: Props) => {
  const [handleUpdate, isUpdating] = useUpdateFlightSegment(flightSegment?.id);
  const [handleDelete, isDeleting] = useDelete(flightSegment.id);
  const { methods } = useFlightSegmentsForm(flightSegment);
  const { isOpen, onClose, onToggle } = useDisclosure();
  const { request, cargoList } = useCurrentRequest();

  const [origin, destination] = useWatch({
    control: methods.control,
    name: ['origin_airport_id', 'destination_airport_id'],
  });

  const handleFlightSegmentDelete = async () => {
    if (!flightSegment?.id) return;
    await handleDelete();
    onToggle();
  };

  const originTimeZone = flightSegment?.origin_airport?.time_zone as TimeZone;
  const destinationTimeZone = flightSegment?.destination_airport?.time_zone as TimeZone;

  const handleUpdateMiddleMan = (values: any) => {
    handleUpdate(values);
  };

  const travelTime = useMemo(() => {
    return modelUtils.flightSegment.duration(flightSegment);
  }, [flightSegment]);

  // Red flag detectors 🚩
  const travelTimeFlagged = useMemo(() => {
    const isFlight = flightSegment.aircraft_type !== AircraftType.RFS;

    // Long RFS detection, per Maruf
    const longDistance =
      flightSegment?.origin_airport?.country === 'US'
        ? miToKm(450) * 1000 // 450 miles in the US
        : miToKm(750) * 1000; // 750 miles outside of the US. Apparently Europe loves long hauls.

    if (!isFlight) {
      const isLongRFS =
        distance(flightSegment?.origin_airport?.latlng, flightSegment?.destination_airport?.latlng) >= longDistance;
      if (isLongRFS) return true; // Long RFS
      return false;
    }

    const isTooShort = travelTime?.durationInMinutes < 0;
    const isTooLong = travelTime?.hours > 20;

    if (isTooLong || isTooShort) return true;

    return false;
  }, [flightSegment, travelTime?.durationInMinutes, travelTime?.hours]);

  const planeTypeFlagged = useMemo(() => {
    const aircraftType = flightSegment.aircraft_type;
    const isPax = modelUtils.flightSegment.isPax(aircraftType);
    const isKnownShipper = request?.is_known_shipper;
    const isDgd = request?.cargo_type === CargoType.DangerousGoods;
    const requiresCao = request?.aircraft_restriction === AircraftRestriction.CAO;
    const isCargoOversized = modelUtils.cargos.isOverSizedForAircraftType(cargoList, aircraftType);

    const departsFromUSA = modelUtils.flightSegment.departsFromUSA(flightSegment);

    if (isPax && isDgd && requiresCao) return true;
    if (isPax && isCargoOversized) return true;

    if (departsFromUSA && !isKnownShipper) {
      if (isPax) return true;
      if (!aircraftType) return true;
    }

    return false;
  }, [flightSegment, request?.is_known_shipper, request?.cargo_type, request?.aircraft_restriction, cargoList]);

  const renderBookingStatusTag = (segment: FlightSegment) => {
    if (segment.booking_status === FlightSegmentBookingStatus.Confirmed) {
      return (
        <Tag size="sm" colorScheme="green">
          <TagLeftIcon as={HiMiniCheckCircle} />
          <TagLabel>{getPrettyFlightSegmentBookingStatus(segment.booking_status)}</TagLabel>
        </Tag>
      );
    }

    if (segment.booking_status === FlightSegmentBookingStatus.Pending) {
      return (
        <Tag size="sm" colorScheme="gray">
          <TagLeftIcon as={HiMiniPauseCircle} />
          <TagLabel>{getPrettyFlightSegmentBookingStatus(segment.booking_status)}</TagLabel>
        </Tag>
      );
    }

    if (segment.booking_status === FlightSegmentBookingStatus.Unknown) {
      return (
        <Tag size="sm" colorScheme="gray">
          <TagLeftIcon as={HiMiniQuestionMarkCircle} />
          <TagLabel>{getPrettyFlightSegmentBookingStatus(segment.booking_status)}</TagLabel>
        </Tag>
      );
    }

    if (segment.booking_status === FlightSegmentBookingStatus.Cancelled) {
      return (
        <Tag size="sm" colorScheme="red">
          <TagLeftIcon as={HiMiniXCircle} />
          <TagLabel>{getPrettyFlightSegmentBookingStatus(segment.booking_status)}</TagLabel>
        </Tag>
      );
    }
    return (
      <Tag size="sm" colorScheme="gray">
        <TagLabel>{getPrettyIATAFlightSegmentStatus(segment.iata_booking_status)}</TagLabel>
      </Tag>
    );
  };

  return (
    <SimpleForm onValid={handleUpdateMiddleMan} defaultValues={flightSegment} {...(methods as UseFormReturn<any>)}>
      <Row className="flight-segment" alignItems="center" sx={{ position: 'relative' }} role="group">
        <AddButton
          quoteId={flightSegment.quote_id}
          providerId={flightSegment.provider_id}
          originAirportId={flightSegment.flight_path_position ? prevDestination : undefined}
          destinationAirportId={flightSegment.flight_path_position ? undefined : origin}
          flightPathPosition={flightSegment.flight_path_position}
          top="-4"
          aria-label="Add Flight Before"
        />
        <FlightSegmentPoint
          isOrigin
          isHighlighted={prevDestination !== undefined && prevDestination !== origin}
          isUpdating={isUpdating}
          initialTimeZone={originTimeZone || defaultOriginTimezone}
        />
        <FlightSegmentPoint
          isHighlighted={nextOrigin !== undefined && nextOrigin !== destination}
          isUpdating={isUpdating}
          initialTimeZone={destinationTimeZone || defaultDestinationTimezone}
        />
        <Column flex={3} borderRight={0}>
          <Stack direction="row" alignItems="center">
            <LabeledText
              flex={1}
              label="Travel Time"
              value={travelTime?.prettyDuration}
              isHighlighted={travelTimeFlagged}
              padding={2}
              borderRadius="10px"
            />
            <SimplePopoverUnifiedInput
              flex={1}
              label="Plane Type"
              names={['aircraft_type', 'aircraft_name']}
              tabIndex={0}
              isHighlighted={planeTypeFlagged}
              highlightColor="red"
            >
              <SimpleSelect ignoreOnBlur name="aircraft_type" options={aircraftTypeOptions} tabIndex={0} />

              <Label label="Aircraft Name" />
              <AircraftInput name="aircraft_name" tabIndex={-1} />
            </SimplePopoverUnifiedInput>
            <SimplePopoverUnifiedInput
              flex={1}
              label="Airline"
              names={['provider_id', 'flight_number']}
              joiner="/"
              tabIndex={!destination ? 0 : -1}
            >
              <AirlineInput name="provider_id" displayValue="id" />
              <SimpleInput name="flight_number" label="Flight Number" tabIndex={!destination ? 0 : -1} ignoreOnBlur />
            </SimplePopoverUnifiedInput>
            <Show if={!!(flightSegment?.booking_status || flightSegment?.iata_booking_status)}>
              <Stack justifyContent="center" alignItems="flex-start">
                <SimplePopoverInput displayValue={renderBookingStatusTag(flightSegment)}>
                  <Stack>
                    <Stack alignItems="flex-end">{renderBookingStatusTag(flightSegment)}</Stack>
                    <Stack direction="row" spacing={4}>
                      <LabeledText label="ID" value={flightSegment.id} />
                      <LabeledText
                        label="Peli Booking Status"
                        value={getPrettyFlightSegmentBookingStatus(flightSegment.booking_status)}
                      />
                      <LabeledText label="Airline Status" value={flightSegment.iata_booking_status} />
                    </Stack>
                    <Stack direction="row" spacing={4}>
                      <LabeledText
                        label="Description"
                        value={getPrettyIATAFlightSegmentStatus(flightSegment.iata_booking_status)}
                      />
                    </Stack>
                  </Stack>
                </SimplePopoverInput>
              </Stack>
            </Show>
            <IconButton
              aria-label="Delete"
              variant="ghost"
              colorScheme="red"
              size="xs"
              icon={<HiTrash />}
              onClick={onToggle}
              opacity={0}
              sx={{ '.flight-segment:hover &': { opacity: 1 } }}
              tabIndex={-1}
            />

            <AlertModal
              isOpen={isOpen}
              onClose={onClose}
              title="Delete Flight Segment"
              message="Are you sure?"
              btnCancel="Cancel"
              btnDelete="Delete"
              handleConfirm={handleFlightSegmentDelete}
              isDeleting={isDeleting}
            />
            <AddButton
              quoteId={flightSegment.quote_id}
              providerId={flightSegment.provider_id}
              originAirportId={destination}
              flightPathPosition={flightSegment.flight_path_position + 1}
              bottom="-4"
              aria-label="Add Flight Segment After"
              tabIndex={!destination ? 0 : -1}
            />
          </Stack>
        </Column>
      </Row>
    </SimpleForm>
  );
};

type RowProps = StackProps & {
  children: ReactNode;
};

export const Row = ({ children, ...rest }: RowProps) => (
  <Stack direction="row" w="full" borderBottomWidth={1} spacing={0} {...rest}>
    {children}
  </Stack>
);

type ColumnProps = StackProps & {
  label?: string;
  children?: ReactNode;
};

export const Column = ({ label, children, ...rest }: ColumnProps) => (
  <Stack direction="column" borderRightWidth={1} p={2} flex={1} {...rest}>
    <Show if={!!label}>
      <Text fontSize="xs" fontWeight="bold">
        {label}
      </Text>
    </Show>
    {children}
  </Stack>
);

type FlightSegmentPointProps = {
  isOrigin?: boolean;
  isHighlighted: boolean;
  isUpdating: boolean;
  initialTimeZone: TimeZone;
};

const FlightSegmentPoint = ({
  isOrigin = false,
  isHighlighted,
  isUpdating,
  initialTimeZone,
}: FlightSegmentPointProps) => {
  const value = useWatch({
    name: isOrigin ? 'origin_airport_id' : 'destination_airport_id',
  });
  const hasValue = !!value;

  return (
    <Column flex={2}>
      <Stack direction="row" alignItems="center">
        <Box w="75px" borderRadius="6px">
          <AirportInput
            name={`${isOrigin ? 'origin' : 'destination'}_airport_id`}
            displayValue="id"
            isDisabled={isUpdating}
            isHighlighted={isHighlighted}
            title={isHighlighted ? `Origin/Destination airport pairs don't match` : ''}
            tabIndex={!hasValue ? 0 : -1}
          />
        </Box>
        <SimpleLabeledPopoverInput
          name={isOrigin ? 'departs_at' : 'arrives_at'}
          inputType="date-picker"
          label="Date/Time"
          initialTimeZone={initialTimeZone}
          showTime
          showClearButton
          flex={1}
          tabIndex={!hasValue ? -1 : 0}
        />
      </Stack>
    </Column>
  );
};

type AddButtonProps = BoxProps & {
  isAbove?: boolean;
  quoteId: number;
  providerId: string;
  originAirportId?: string;
  destinationAirportId?: string;
  flightPathPosition: number;
};

const AddButton = ({
  quoteId,
  providerId,
  originAirportId,
  destinationAirportId,
  flightPathPosition,
  ...rest
}: AddButtonProps) => {
  const bgColor = useColorModeValue('white', 'gray.700');

  const { create, isLoading } = useCreateFlightSegment(quoteId);

  const isDisabled = originAirportId === null || destinationAirportId === null;

  const handleFlightSegmentCreate = async () => {
    if (!quoteId) return;

    await create({
      provider_id: providerId,
      origin_airport_id: originAirportId,
      destination_airport_id: destinationAirportId,
      flight_path_position: flightPathPosition,
    });
  };

  return (
    <Box
      title={isDisabled ? 'Either Origin or Destination is not set' : undefined}
      position="absolute"
      left="50%"
      transform="translateX(-50%)"
      borderRadius="50%"
      bg={bgColor}
      w="32px"
      h="32px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      opacity={0}
      pointerEvents="none"
      _groupHover={{
        opacity: 1,
        pointerEvents: 'auto',
      }}
      {...rest}
    >
      <IconButton
        icon={<HiPlus />}
        variant="ghost"
        size="xs"
        borderRadius="50%"
        onClick={handleFlightSegmentCreate}
        isDisabled={isDisabled}
        _hover={isDisabled ? {} : undefined}
        _active={{}}
        isLoading={isLoading}
        aria-label="Add Flight Segment"
      />
    </Box>
  );
};
